import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();

    const abortController = this.#createAbortController();

    try {
  // 1) Intercept influencer codes first
  /** @type {{ code: string; gifts: number[]; discountOneTime?: string; discountSubscription?: string; eligProducts?: number[]; eligCollectionProducts?: number[]; autoAdd?: boolean; scheduleStart?: string; scheduleEnd?: string; }[] } */
  // @ts-ignore - custom global injected by influencer-offers block
  const __offers = window.__influencerOffers || [];
  const influencer = __offers.find?.((o) => o && typeof o.code === 'string' && o.code.toLowerCase() === discountCodeValue.toLowerCase());

      if (influencer) {
        cartDiscountError.classList.add('hidden');
        cartDiscountErrorDiscountCode.classList.add('hidden');
        cartDiscountErrorShipping.classList.add('hidden');

        const gifts = Array.isArray(influencer.gifts) ? influencer.gifts : [];
        // Eligibility: respect product list and collection product ids if configured
        try {
          const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, signal: abortController.signal }).then((r) => r.json());
          /** @type {any[]} */
          const items = Array.isArray(cart && cart.items) ? /** @type {any[]} */ (cart.items) : [];
          // Compute non-GWP subtotal so influencer gifts themselves don't satisfy threshold
          // Exclude digital products (those that don't require shipping) from GWP threshold
          // Use line_price to reflect current quantity (already price * quantity)
          let nonGwpTotal = 0;
          for (let i = 0; i < items.length; i++) {
            const it = items[i] || {};
            const props = it.properties || {};
            const requiresShipping = it.requires_shipping !== false; // Default to true if undefined
            const isLoyaltyProduct = !!(props && props.__lion_sfp_id); // Loyalty products discounted to 0
            if (!(props && props._is_gwp === 'true') && requiresShipping && !isLoyaltyProduct) {
              // Use line_price to reflect current quantity in cart
              const lp = Number(it.line_price || 0);
              if (!Number.isNaN(lp)) nonGwpTotal += lp;
            }
          }
          // Threshold enforcement (in cents)
          try {
            const threshold = Number((/** @type {any} */ (influencer) && /** @type {any} */ (influencer).threshold) || 0) || 0;
            if (threshold > 0 && nonGwpTotal < threshold) {
              cartDiscountError.classList.remove('hidden');
              cartDiscountErrorDiscountCode.classList.remove('hidden');
              const msg = this.refs.cartDiscountErrorDiscountCode?.querySelector?.('.cart-discount__error-text');
              if (msg) msg.textContent = 'This code is not eligible for your current cart total.';
              return;
            }
          } catch (_) {}
          const inCartProductIds = items.map((it) => Number(it && it.product_id || 0)).filter((n) => !Number.isNaN(n) && n > 0);
          if (Array.isArray(influencer.eligProducts) && influencer.eligProducts.length) {
            const eligSkus = Array.isArray(influencer.eligSkus) && influencer.eligSkus.length ? influencer.eligSkus : [];
            let ok;
            if (eligSkus.length > 0) {
              // Must match both product ID and SKU
              ok = items.some((it) => {
                const pid = Number(it && it.product_id || 0);
                const sku = String(it && it.sku || '').trim().toUpperCase();
                return pid > 0 && influencer.eligProducts.includes(pid) && sku && eligSkus.includes(sku);
              });
            } else {
              ok = influencer.eligProducts.some((pid) => inCartProductIds.includes(Number(pid)));
            }
            if (!ok) {
              cartDiscountError.classList.remove('hidden');
              cartDiscountErrorDiscountCode.classList.remove('hidden');
              const msg = this.refs.cartDiscountErrorDiscountCode?.querySelector?.('.cart-discount__error-text');
              if (msg) msg.textContent = 'This code is not eligible for your current items.';
              return;
            }
          }
          if (Array.isArray(influencer.eligCollectionProducts) && influencer.eligCollectionProducts.length) {
            const okc = influencer.eligCollectionProducts.some((pid) => inCartProductIds.includes(Number(pid)));
            if (!okc) {
              cartDiscountError.classList.remove('hidden');
              cartDiscountErrorDiscountCode.classList.remove('hidden');
              const msg = this.refs.cartDiscountErrorDiscountCode?.querySelector?.('.cart-discount__error-text');
              if (msg) msg.textContent = 'This code is not eligible for your current items.';
              return;
            }
          }
          // Schedule: if window set, block outside of the time window (Europe/London)
          if (influencer.scheduleStart || influencer.scheduleEnd) {
            const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
            const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
            const now = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
            /**
             * @param {string|undefined|null} s
             * @param {boolean} end
             */
            const norm = (s, end) => {
              if (!s) return undefined;
              const stripped = String(s).trim().replace(' ', 'T').replace(/Z$/i, '').replace(/[+-]\d{2}:?\d{2}$/i, '');
              const parts2 = stripped.split('T');
              const date = parts2[0] || '';
              let time = parts2[1] || (end ? '23:59' : '00:00');
              if (/^\d{2}:\d{2}:\d{2}$/.test(time)) time = time.slice(0,5);
              if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
              if (!/^\d{2}:\d{2}$/.test(time)) return undefined;
              return `${date}T${time}`;
            };
            const start = norm(influencer.scheduleStart, false);
            const end = norm(influencer.scheduleEnd, true);
            if ((start && now < start) || (end && now > end) || (start && end && start > end)) {
              cartDiscountError.classList.remove('hidden');
              cartDiscountErrorDiscountCode.classList.remove('hidden');
              const msg = this.refs.cartDiscountErrorDiscountCode?.querySelector?.('.cart-discount__error-text');
              if (msg) msg.textContent = 'This code is not currently active.';
              return;
            }
          }
        } catch (_) { /* if checks fail, proceed conservatively */ }
        if (gifts.length > 0) {
          // Check stacking setting
          let allowStacking = false;
          try {
            const T = /** @type {any} */ (typeof window !== 'undefined' && window.Theme ? window.Theme : {});
            allowStacking = !!(T && T.flags && T.flags.allowGwpInfluencerStacking === true);
          } catch (_) { allowStacking = false; }

          // If stacking is disabled and there's already any gift in cart, block influencer gift add
          if (!allowStacking) {
            try {
              const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, signal: abortController.signal }).then((r) => r.json());
              /** @type {any[]} */
              const items = Array.isArray(cart && cart.items) ? /** @type {any[]} */ (cart.items) : [];
              let hasAnyGift = false;
              for (let i = 0; i < items.length; i++) {
                const it = items[i] || {};
                const props = it.properties || {};
                if (props && props._is_gwp === 'true') { hasAnyGift = true; break; }
              }
              if (hasAnyGift) {
                // Show an error and don't add influencer gifts
                cartDiscountError.classList.remove('hidden');
                cartDiscountErrorDiscountCode.classList.remove('hidden');
                const msg = this.refs.cartDiscountErrorDiscountCode?.querySelector?.('.cart-discount__error-text');
                if (msg) msg.textContent = 'Only one gift promotion can be used per order.';
                return;
              }
            } catch (_) {
              // If cart check fails, be safe and block to avoid stacking
              cartDiscountError.classList.remove('hidden');
              cartDiscountErrorDiscountCode.classList.remove('hidden');
              const msg = this.refs.cartDiscountErrorDiscountCode?.querySelector?.('.cart-discount__error-text');
              if (msg) msg.textContent = 'Only one gift promotion can be used per order.';
              return;
            }
          }
          // Add influencer gifts as GWP items (quantity 1) then refresh cart; also apply configured discount if present
          const configAdd = fetchConfig('json', {
            body: JSON.stringify({
              items: gifts.map((/** @type {number} */ id) => ({ id: Number(id), quantity: 1, properties: { _is_gwp: 'true', _gwp_group: `influencer:${discountCodeValue.toLowerCase()}` } })),
              sections: [this.dataset.sectionId],
            }),
          });

          await fetch('/cart/add.js', { ...configAdd, signal: abortController.signal });
          // Choose which discount code to apply (subscription-aware)
          let chosenCode = '';
          try {
            const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, signal: abortController.signal }).then((r) => r.json());
            /** @type {any[]} */
            const items2 = Array.isArray(cart && cart.items) ? cart.items : [];
            const hasSub = items2.some((/** @type {any} */ it) => it && (it.selling_plan_allocation || it.selling_plan_id || (it.properties && (it.properties.selling_plan || it.properties._rc))));
            chosenCode = hasSub ? (influencer.discountSubscription || influencer.discountOneTime || '') : (influencer.discountOneTime || '');
          } catch(_) { chosenCode = influencer.discountOneTime || ''; }
          if (chosenCode) {
            try {
              const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, signal: abortController.signal }).then((r) => r.json());
              /** @type {Array<{code:string}>} */
              const existing = Array.isArray(cart && cart.discount_codes) ? cart.discount_codes : [];
              const codes = existing.map((/** @type {{code:string}} */ d) => (d && d.code) ? String(d.code) : '').filter(Boolean);
              if (!codes.map((/** @type {string} */ c) => c.toLowerCase()).includes(chosenCode.toLowerCase())) codes.push(chosenCode);
              await fetch(Theme.routes.cart_update_url, { ...fetchConfig('json', { body: JSON.stringify({ discount: codes.join(',') }) }), signal: abortController.signal });
            } catch(_) {}
          }
          discountCode.value = '';
          document.location.reload();
          return;
        } else {
          // Intercept to avoid incorrect Shopify discount error when code is influencer but no gifts set
          cartDiscountError.classList.remove('hidden');
          cartDiscountErrorDiscountCode.classList.remove('hidden');
          // Optional: customize text
          const msg = this.refs.cartDiscountErrorDiscountCode?.querySelector?.('.cart-discount__error-text');
          if (msg) msg.textContent = 'This influencer code has no gifts configured.';
          return;
        }
      }

      // 2) Fallback to standard discount flow
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

  const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
